/**
 * useRecordingState — State machine hook for the Recording feature.
 *
 * States: idle → expanded → frameSelect → recording → finished → idle
 *
 * This hook owns **all** recording-related UI state; the actual
 * MediaRecorder / canvas-capture logic is intentionally omitted (P1).
 */

import { useState, useCallback, useRef, useEffect } from "react";

import {
  startScreenRecording,
  stopRecording as stopRecordingService,
  convertToMp4ViaBackend,
  uploadRecording as _uploadRecording,
  type RecordingMetadata as _RecordingMetadata,
} from "../data/recordingService";

function isWindows(): boolean {
  return navigator.userAgent.indexOf("Win") !== -1;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type RecordingPhase =
  | "idle" // State A – collapsed Record button
  | "expanded" // State B – button group expanded
  | "frameSelect" // State C – green frame, Cancel / Start
  | "recording" // State D – red frame, REC / timer / Pause / Stop
  | "finished"; // State E – Download / Discard

export type AspectRatio = "16:9" | "4:3" | "3:4" | "9:16" | "1:1" | "custom";

/** Settings for one-click video generation (Video Settings modal). */
export interface VideoGenSettings {
  quality: "720p" | "1080p" | "4k";
  targetDurationMinutes: number | null;
  voice: string;
}

export interface RecordingSettings {
  aspectRatio: AspectRatio;
  cornerRadius: number; // 0–32 px
  showCamera: boolean;
  cameraSize: number; // px (80–300)
  microphone: string; // device label / id (UI-only)
  canvasPadding: number; // 0–160 px
  showCursorHighlight: boolean;
  cursorColor: string; // hex
}

export interface FrameRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface RecordingState {
  // Phase
  phase: RecordingPhase;

  // Settings
  settings: RecordingSettings;

  // Teleprompter
  isTeleprompterOpen: boolean;
  teleprompterScript: string;
  teleprompterSpeed: number; // 1–10
  teleprompterOpacity: number; // 0–100
  isTeleprompterPlaying: boolean;

  // Settings modal
  isSettingsOpen: boolean;

  // Video Settings modal (one-click video)
  isVideoSettingsOpen: boolean;

  // Frame
  frame: FrameRect;

  // Recording timer (seconds elapsed)
  elapsedSeconds: number;
  isPaused: boolean;

  // Upload state
  isUploading: boolean;

  // Conversion state
  isConverting: boolean;
}

export interface RecordingActions {
  // Phase transitions
  expand: () => void;
  collapse: () => void;
  enterFrameSelect: () => void;
  cancelFrameSelect: () => void;
  startRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  stopRecording: () => void;
  download: () => void;
  discard: () => void;
  /** Set video blob and switch to finished phase (e.g. after one-click video). User then clicks Download or Discard. */
  setFinishedVideo: (blob: Blob) => void;

  // Settings
  openSettings: () => void;
  closeSettings: () => void;
  updateSettings: (partial: Partial<RecordingSettings>) => void;

  // Video Settings (one-click video)
  openVideoSettings: () => void;
  closeVideoSettings: () => void;

  // Teleprompter
  toggleTeleprompter: () => void;
  closeTeleprompter: () => void;
  setTeleprompterScript: (text: string) => void;
  setTeleprompterSpeed: (speed: number) => void;
  setTeleprompterOpacity: (opacity: number) => void;
  toggleTeleprompterPlayback: () => void;

  // Frame
  updateFrame: (rect: Partial<FrameRect>) => void;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_SETTINGS: RecordingSettings = {
  aspectRatio: "16:9",
  cornerRadius: 16,
  showCamera: true, // Avatar bubble enabled by default
  cameraSize: 150, // Medium — matches discrete button options
  microphone: "default",
  canvasPadding: 80,
  showCursorHighlight: true,
  cursorColor: "#ef4444", // red
};

const DEFAULT_FRAME: FrameRect = {
  x: 0,
  y: 0,
  width: 640,
  height: 360,
};

// Screen-edge margins used consistently across drag, resize, and initial sizing
const GUI_TOP = 72; // Header: 16px offset + 48px height + 8px gap
const EDGE_MARGIN = 24; // Left / right / bottom safety margin (px)

/** Numeric w/h ratio for a given AspectRatio preset, or null for custom. */
function ratioValue(ar: AspectRatio): number | null {
  switch (ar) {
    case "16:9":
      return 16 / 9;
    case "4:3":
      return 4 / 3;
    case "3:4":
      return 3 / 4;
    case "9:16":
      return 9 / 16;
    case "1:1":
      return 1;
    case "custom":
      return null;
  }
}

/**
 * Compute the largest frame for the given aspect ratio that fits the
 * current viewport, centred on screen and respecting all edge margins.
 * For "custom" mode this returns a sensible 640×360 fallback.
 */
function computeMaxFrame(ar: AspectRatio): FrameRect {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const maxW = vw - EDGE_MARGIN * 2;
  const maxH = vh - GUI_TOP - EDGE_MARGIN;

  const r = ratioValue(ar);

  let w: number;
  let h: number;

  if (r !== null) {
    // Start with full available width, derive height
    w = maxW;
    h = w / r;
    // If that exceeds available height, fit to height instead
    if (h > maxH) {
      h = maxH;
      w = h * r;
    }
  } else {
    // custom — default 640×360 clamped to viewport
    w = 640;
    h = 360;
    if (w > maxW) {
      const s = maxW / w;
      w *= s;
      h *= s;
    }
    if (h > maxH) {
      const s = maxH / h;
      w *= s;
      h *= s;
    }
  }

  w = Math.round(w);
  h = Math.round(h);

  // Centre vertically within the drag-safe zone [GUI_TOP … vh - EDGE_MARGIN]
  const frameY = Math.round(GUI_TOP + (vh - GUI_TOP - h - EDGE_MARGIN) / 2);

  return {
    x: Math.round((vw - w) / 2),
    y: Math.max(GUI_TOP, frameY),
    width: w,
    height: h,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useRecordingState(): RecordingState & RecordingActions {
  const [phase, setPhase] = useState<RecordingPhase>("idle");
  const [settings, setSettings] = useState<RecordingSettings>(DEFAULT_SETTINGS);

  // Teleprompter
  const [isTeleprompterOpen, setIsTeleprompterOpen] = useState(false);
  const [teleprompterScript, setTeleprompterScript] = useState("");
  const [teleprompterSpeed, setTeleprompterSpeed] = useState(5);
  const [teleprompterOpacity, setTeleprompterOpacity] = useState(85);
  const [isTeleprompterPlaying, setIsTeleprompterPlaying] = useState(false);

  // Settings modal
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Video Settings modal (one-click video)
  const [isVideoSettingsOpen, setIsVideoSettingsOpen] = useState(false);

  // Frame
  const [frame, setFrame] = useState<FrameRect>(DEFAULT_FRAME);

  // Timer
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Recording state
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null,
  );
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [isUploading, _setIsUploading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const recordingStartTime = useRef<number | null>(null);

  // Timer management
  useEffect(() => {
    if (phase === "recording" && !isPaused) {
      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [phase, isPaused]);

  // ── Phase transitions ─────────────────────────────────────────────────────

  const expand = useCallback(() => setPhase("expanded"), []);
  const collapse = useCallback(() => {
    setPhase("idle");
    setIsTeleprompterOpen(false);
    setIsSettingsOpen(false);
    setIsVideoSettingsOpen(false);
  }, []);

  const enterFrameSelect = useCallback(() => {
    setFrame(computeMaxFrame(settings.aspectRatio));
    setPhase("frameSelect");
  }, [settings.aspectRatio]);

  // When the aspect ratio changes while already in frameSelect,
  // recompute the frame immediately so the user sees instant feedback.
  useEffect(() => {
    if (phase === "frameSelect") {
      setFrame(computeMaxFrame(settings.aspectRatio));
    }
    // Only react to aspectRatio changes, not phase changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.aspectRatio]);

  const cancelFrameSelect = useCallback(() => setPhase("expanded"), []);

  const startRecording = useCallback(async () => {
    try {
      // Start MediaRecorder with optional microphone
      const micId =
        settings.microphone === "none" ? "none" : settings.microphone;
      const recorder = await startScreenRecording(frame, micId);
      setMediaRecorder(recorder);

      // Start recording
      recorder.start(1000); // Collect data every 1 second
      recordingStartTime.current = Date.now();

      setElapsedSeconds(0);
      setIsPaused(false);
      setPhase("recording");
    } catch (error) {
      console.error("[Recording] Failed to start:", error);
      alert("Failed to start recording. Please check permissions.");
      setPhase("frameSelect");
    }
  }, [frame, settings.microphone]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.pause();
      setIsPaused(true);
    }
  }, [mediaRecorder]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorder && mediaRecorder.state === "paused") {
      mediaRecorder.resume();
      setIsPaused(false);
    }
  }, [mediaRecorder]);

  const stopRecording = useCallback(async () => {
    if (!mediaRecorder) {
      console.warn("[Recording] No recorder to stop");
      alert("No active recording found. Please start recording first.");
      return;
    }

    try {
      // Stop recording and get video blob
      const blob = await stopRecordingService(mediaRecorder);
      setVideoBlob(blob);
      setMediaRecorder(null);

      // Calculate duration
      const duration = recordingStartTime.current
        ? Math.floor((Date.now() - recordingStartTime.current) / 1000)
        : elapsedSeconds;

      setElapsedSeconds(duration);
      setIsPaused(false);
      setPhase("finished");
    } catch (error) {
      console.error("[Recording] Failed to stop:", error);
      alert("Failed to stop recording");
      setPhase("recording");
    }
  }, [mediaRecorder, elapsedSeconds]);

  const download = useCallback(
    async (_currentDiagramId?: string) => {
      if (!videoBlob) {
        console.error("[Recording] No video to download");
        alert("No video to download");
        return;
      }

      try {
        // Convert to MP4 if needed (webm → mp4, or on Windows: mp4+opus → mp4+aac)
        let downloadBlob = videoBlob;
        const needsConversion =
          !videoBlob.type.includes("mp4") ||
          (isWindows() && videoBlob.type.includes("opus"));
        if (needsConversion) {
          setIsConverting(true);
          try {
            downloadBlob = await convertToMp4ViaBackend(videoBlob);
          } catch (convertError) {
            console.warn(
              "[Recording] MP4 conversion failed, downloading original:",
              convertError,
            );
          } finally {
            setIsConverting(false);
          }
        }

        // Create download link
        const url = URL.createObjectURL(downloadBlob);
        const a = document.createElement("a");
        a.href = url;
        const ext = downloadBlob.type.includes("mp4") ? "mp4" : "webm";
        a.download = `recording-${new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/:/g, "-")}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        // Reset state
        setVideoBlob(null);
        setPhase("idle");
        setIsTeleprompterOpen(false);
        setElapsedSeconds(0);
      } catch (error) {
        console.error("[Recording] Download failed:", error);
        alert(
          `Failed to download recording:\n\n${
            error instanceof Error ? error.message : "Unknown error"
          }\n\nPlease try again.`,
        );
      }
    },
    [videoBlob],
  );

  const discard = useCallback(() => {
    if (videoBlob) {
      setVideoBlob(null);
    }

    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
      setMediaRecorder(null);
    }

    setPhase("idle");
    setIsTeleprompterOpen(false);
    setElapsedSeconds(0);
  }, [videoBlob, mediaRecorder]);

  /** Set video blob and switch to finished phase (e.g. one-click video). Same Download/Discard UI as recording. */
  const setFinishedVideo = useCallback((blob: Blob) => {
    setVideoBlob(blob);
    setPhase("finished");
  }, []);

  // ── Settings ──────────────────────────────────────────────────────────────

  const openSettings = useCallback(() => setIsSettingsOpen(true), []);
  const closeSettings = useCallback(() => setIsSettingsOpen(false), []);
  const openVideoSettings = useCallback(() => setIsVideoSettingsOpen(true), []);
  const closeVideoSettings = useCallback(() => setIsVideoSettingsOpen(false), []);
  const updateSettings = useCallback(
    (partial: Partial<RecordingSettings>) =>
      setSettings((prev) => ({ ...prev, ...partial })),
    [],
  );

  // ── Teleprompter ──────────────────────────────────────────────────────────

  const toggleTeleprompter = useCallback(
    () => setIsTeleprompterOpen((prev) => !prev),
    [],
  );
  const closeTeleprompter = useCallback(() => setIsTeleprompterOpen(false), []);
  const toggleTeleprompterPlayback = useCallback(
    () => setIsTeleprompterPlaying((prev) => !prev),
    [],
  );

  // ── Frame ─────────────────────────────────────────────────────────────────

  const updateFrame = useCallback(
    (rect: Partial<FrameRect>) => setFrame((prev) => ({ ...prev, ...rect })),
    [],
  );

  return {
    // State
    phase,
    settings,
    isTeleprompterOpen,
    teleprompterScript,
    teleprompterSpeed,
    teleprompterOpacity,
    isTeleprompterPlaying,
    isSettingsOpen,
    isVideoSettingsOpen,
    frame,
    elapsedSeconds,
    isPaused,
    isUploading,
    isConverting,
    // Actions
    expand,
    collapse,
    enterFrameSelect,
    cancelFrameSelect,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    download,
    discard,
    setFinishedVideo,
    openSettings,
    closeSettings,
    updateSettings,
    openVideoSettings,
    closeVideoSettings,
    toggleTeleprompter,
    closeTeleprompter,
    setTeleprompterScript,
    setTeleprompterSpeed,
    setTeleprompterOpacity,
    toggleTeleprompterPlayback,
    updateFrame,
  };
}
